import React, { useRef } from "react";
import * as Styles from "./styles/CompareHeader";
import * as CompareItemStyles from "./styles/CompareItem";
import { getCarNameObject } from "../../utils/formatters/nameFormatters";
import Icon, { IconChevronUp } from "../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { type CompareV2DispatchType, useCompareV2Selector } from "../../redux/store";
import SelectGrade from "./select-grade/SelectGrade";
import { ToggleIconWrapper } from "../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import useNewCarHeader from "../../hooks/useNewCarHeader";
import { CompareV2Label, useCompareV2Label } from "../../utils/labels";
import useMaxNumberToCompare from "../../../../shared-logic/features/compare/hooks/useMaxNumberToCompare";
import CompareTable from "../common/CompareTable";
import ButtonPrimary from "../../../../common-deprecated/components/aem/lexus/button/ButtonPrimary";
import ButtonTertiary from "../../../../common-deprecated/components/aem/lexus/button/ButtonTertiary";
import { showModal } from "../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_DELETE_CAR } from "../../../../shared-logic/features/compare/utils/modalConstants";
import { CompareV2Mode } from "../../../../shared-logic/features/compare/utils/types";
import { isRTL } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import { useDispatch } from "../../../../shared-logic/redux/store";

type NewCompareHeaderPropsType = {
    expanded: boolean;
    toggleExpanded: () => void;
    onShowSelectNewGrade: (index: number) => void;
    scrollbarWidth: number;
};

const NewCarHeader = (props: NewCompareHeaderPropsType): JSX.Element => {
    const { expanded, toggleExpanded, onShowSelectNewGrade, scrollbarWidth } = props;

    const dispatch = useDispatch<CompareV2DispatchType>();
    const cars = useCompareV2Selector((state) => state.newCarCompare.grades);
    const settings = useCompareV2Selector((state) => state.commonSettings);
    const compareV2Settings = useCompareV2Selector((state) => state.compareV2Settings);
    const isRTLSetting = isRTL(settings);

    const { compareV2Standalone } = compareV2Settings;

    const { borderClickHandler, expandToggleClickHandler, clearAll, selectGradeOpenIndex, setSelectGradeOpenIndex } =
        useNewCarHeader(expanded, toggleExpanded, onShowSelectNewGrade, cars, true);

    const maxNumberToCompare = useMaxNumberToCompare();
    const expandedLabel = useCompareV2Label("compareBodyTopSelectCar");

    const compareHeaderRef = useRef<HTMLDivElement>(null);
    return (
        <Styles.Wrapper ref={compareHeaderRef} expanded={expanded} scrollbarWidth={scrollbarWidth}>
            <Styles.Actions align={isRTL(settings) ? "right" : "left"} hidden={compareV2Standalone}>
                {(cars.length > 1 || expanded) && !compareV2Standalone && (
                    <ButtonPrimary onClick={expandToggleClickHandler} linkWithoutIcon hasIcon>
                        <span className="t-base-text l-micro-text">
                            <CompareV2Label label={expanded ? "compareHeaderHide" : "compareHeaderTitle"} />
                        </span>
                        <ToggleIconWrapper
                            ml={isRTLSetting ? 1 : 0}
                            mr={isRTLSetting ? 0 : 1}
                            iconColor="white"
                            isDown={expanded}
                        >
                            <IconChevronUp />
                        </ToggleIconWrapper>
                    </ButtonPrimary>
                )}
            </Styles.Actions>

            <Styles.ItemWrapper isStandalone={compareV2Standalone} role="rowgroup">
                <CompareTable
                    items={cars}
                    heading
                    columnChild={(car, index) => {
                        const { title, subtitle } = getCarNameObject(car, settings);
                        return (
                            <CompareItemStyles.Border
                                onClick={() => borderClickHandler(index)}
                                showCursorPointer={expanded}
                                hideBorder={index + 1 === maxNumberToCompare}
                            >
                                <Styles.MaskedPreloader show={car.loading} />
                                <SelectGrade
                                    index={index}
                                    opened={index === selectGradeOpenIndex}
                                    setOpenState={(open: boolean) => setSelectGradeOpenIndex(open ? index : -1)}
                                    title={title}
                                    subtitle={subtitle}
                                    compareHeaderRef={compareHeaderRef}
                                />
                                <CompareItemStyles.IconHolder>
                                    <CompareItemStyles.CloseButton
                                        onClick={(event) => {
                                            if (!expanded) {
                                                event.stopPropagation();
                                                dispatch(
                                                    showModal({
                                                        type: MODAL_DELETE_CAR,
                                                        mode: CompareV2Mode.New,
                                                        indexToDelete: index,
                                                    }),
                                                );
                                            }
                                        }}
                                    >
                                        <Icon variant={expanded ? "chevronDown" : "close"} />
                                    </CompareItemStyles.CloseButton>
                                </CompareItemStyles.IconHolder>
                            </CompareItemStyles.Border>
                        );
                    }}
                    placeholder={() => (
                        <CompareItemStyles.Border
                            showCursorPointer
                            onClick={() => borderClickHandler(cars.length)}
                            hideBorder
                        >
                            <CompareItemStyles.Title>
                                <SelectGrade
                                    index={cars.length}
                                    opened={cars.length === selectGradeOpenIndex}
                                    setOpenState={(open: boolean) => setSelectGradeOpenIndex(open ? cars.length : -1)}
                                    title={expandedLabel}
                                    compareHeaderRef={compareHeaderRef}
                                />
                            </CompareItemStyles.Title>
                            <CompareItemStyles.IconHolder>
                                <Icon variant="chevronDown" />
                            </CompareItemStyles.IconHolder>
                        </CompareItemStyles.Border>
                    )}
                />
            </Styles.ItemWrapper>

            <Styles.Actions align={isRTL(settings) ? "left" : "right"}>
                <ButtonTertiary onClick={() => clearAll()}>
                    <CompareV2Label label="compareHeaderClearAll" />
                </ButtonTertiary>
            </Styles.Actions>
        </Styles.Wrapper>
    );
};

export default NewCarHeader;
